import {
  AddAuditorFirmMemberPayload,
  ArchiveAuditDto,
  AuditArchiveJobResponse,
  AuditorFirmMemberDto,
  CreateAuditDto,
  UpdateAuditDto,
  InviteTeamMemberPayload,
  UpdateTeamMemberPayload,
  AuditRole,
} from '@/api';
import { auditKeys, companyKeys, documentKeys, statusKeys } from '@shared/api/query-keys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { auditsApi } from './audits';

export const useAudits = () =>
  useQuery({
    queryKey: auditKeys.list(),
    queryFn: () => auditsApi.getAudits(),
  });

export const useAudit = (auditId?: string, enabled = true) =>
  useQuery({
    queryKey: auditKeys.detail(auditId),
    queryFn: () => auditsApi.getAudit(auditId as string),
    enabled: Boolean(auditId) && enabled,
  });

export const useIsAuditArchived = (): boolean => {
  const { id } = useParams<Record<string, string>>();
  const { data } = useAudit(id);
  return Boolean(data?.audit.archived || data?.audit.pendingArchive);
};

export const useMyTeamQuery = (auditId?: string) =>
  useQuery({
    queryKey: auditKeys.myTeam(auditId),
    queryFn: () => auditsApi.getMyTeam(auditId as string),
    enabled: Boolean(auditId),
  });

export const useAuditAuditorFirmMembers = (auditId?: string) =>
  useQuery({
    queryKey: auditKeys.auditorFirmMembers(auditId),
    queryFn: () => auditsApi.getAuditorFirmMembers(auditId as string),
    enabled: Boolean(auditId),
  });

export const useAuditTeamMembers = (auditId?: string) =>
  useQuery({
    queryKey: auditKeys.teamMembers(auditId),
    queryFn: () => auditsApi.getTeamMembers(auditId as string),
    enabled: Boolean(auditId),
  });

/** `GET /audit/:id/participants` is mounted behind `documentAccess`: pass that gate as `enabled`. */
export const useAuditParticipants = (auditId?: string, enabled = true) =>
  useQuery({
    queryKey: auditKeys.participants(auditId),
    queryFn: () => auditsApi.getParticipants(auditId as string),
    enabled: Boolean(auditId) && enabled,
  });

export const useDeleteTeamMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ auditId, memberId }: { auditId: string; memberId: string }) =>
      auditsApi.deleteTeamMember(auditId as string, memberId as string),
    onSuccess: (_, { auditId }) => {
      queryClient.invalidateQueries({ queryKey: auditKeys.teamMembers(auditId) });
    },
  });
};

export const useMyTeamRole = (auditId?: string) =>
  useQuery({
    queryKey: auditKeys.myRole(auditId),
    queryFn: () => auditsApi.getMyTeamRole(auditId as string),
    enabled: Boolean(auditId),
  });

export const useInviteTeamMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ auditId, data }: { auditId: string; data: InviteTeamMemberPayload }) =>
      auditsApi.inviteTeamMember(auditId as string, data),
    onSuccess: (_, { auditId }) => {
      queryClient.invalidateQueries({ queryKey: auditKeys.teamMembers(auditId) });
    },
  });
};

export const useUpdateAuditTeamMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      auditId,
      memberId,
      data,
    }: {
      auditId: string;
      memberId: string;
      data: UpdateTeamMemberPayload;
    }) => auditsApi.updateTeamMember(auditId as string, memberId as string, data),
    onSuccess: (_, { auditId }) => {
      queryClient.invalidateQueries({ queryKey: auditKeys.detail(auditId) });
      queryClient.invalidateQueries({ queryKey: auditKeys.teamMembers(auditId) });
    },
  });
};

export const useCreateAudit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAuditDto) => auditsApi.createAudit(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditKeys.list() });
      queryClient.invalidateQueries({ queryKey: companyKeys.detail() });
    },
  });
};

export const useUpdateAudit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ auditId, data }: { auditId: string; data: UpdateAuditDto }) =>
      auditsApi.updateAudit(auditId, data),
    onSuccess: (_, { auditId }) => {
      queryClient.invalidateQueries({ queryKey: auditKeys.list() });
      queryClient.invalidateQueries({ queryKey: auditKeys.detail(auditId) });
      // The Dashboard's status card reads the audit's fields (the report date among them)
      // from its own root, which neither key above reaches: it showed the old date until
      // a full reload (#525).
      queryClient.invalidateQueries({ queryKey: auditKeys.status(auditId) });
      // The Status page reads them from the status board's overview, a root none of the
      // above reach either. While the page stays open its own overlay shows the saved value;
      // back from another page that overlay is gone and the pre-save copy, still fresh for
      // the 5-minute staleTime, showed the old date or "Select date" (#525).
      queryClient.invalidateQueries({ queryKey: statusKeys.overview(auditId) });
    },
  });
};

export const useDeleteAudit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (auditId: string) => auditsApi.deleteAudit(auditId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditKeys.list() });
    },
  });
};

export const useArchiveAudit = () => {
  const queryClient = useQueryClient();
  return useMutation<
    AuditArchiveJobResponse & { message?: string },
    Error,
    { auditId: string; data: ArchiveAuditDto }
  >({
    mutationFn: ({ auditId, data }) => auditsApi.archiveAudit(auditId, data),
    // POST /archive freezes the audit (pendingArchive=true) and returns immediately while the
    // report PDF is built by the background worker. Refetch the audit so the archivation banner
    // reflects the frozen state right away instead of lagging until the 5-min staleTime expires.
    onSuccess: (_res, { auditId }) => {
      queryClient.invalidateQueries({ queryKey: auditKeys.detail(auditId) });
      queryClient.invalidateQueries({ queryKey: auditKeys.list() });
    },
  });
};

export const useArchiveStatus = (auditId?: string, jobId?: number | null, enabled = true) =>
  useQuery<AuditArchiveJobResponse | null, Error>({
    queryKey: auditKeys.archiveStatus(auditId, jobId),
    queryFn: () => auditsApi.archiveStatus(auditId as string, jobId),
    enabled: Boolean(auditId) && enabled,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'pending' || status === 'running' ? 2000 : false;
    },
  });

export const useAuditStatus = (auditId?: string, enabled: boolean = true) =>
  useQuery({
    queryKey: auditKeys.status(auditId),
    queryFn: () => auditsApi.getStatus(auditId as string),
    enabled: Boolean(auditId) && enabled,
  });

export const useAuditRoles = (auditId?: string, enabled?: boolean) =>
  useQuery({
    queryKey: auditKeys.roles(auditId),
    queryFn: () => auditsApi.getRoles(auditId as string),
    enabled: Boolean(auditId) && enabled,
  });

export const useAuditRole = (auditId?: string, roleId?: string, enabled?: boolean) =>
  useQuery({
    queryKey: auditKeys.role(auditId, roleId),
    queryFn: () => auditsApi.getRole(auditId as string, roleId as string),
    enabled: Boolean(auditId) && Boolean(roleId) && enabled,
  });

export const useCreateAuditRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ auditId, payload }: { auditId: string; payload: AuditRole }) =>
      auditsApi.createRole(auditId, payload),
    onSuccess: (_, { auditId }) => {
      queryClient.invalidateQueries({ queryKey: auditKeys.detail(auditId) });
      queryClient.invalidateQueries({ queryKey: auditKeys.roles(auditId) });
    },
  });
};

export const useUpdateAuditRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      auditId,
      roleId,
      payload,
    }: {
      auditId: string;
      roleId: string;
      payload: AuditRole;
    }) => auditsApi.updateRole(auditId, roleId, payload),
    onSuccess: (_, { auditId, roleId }) => {
      queryClient.invalidateQueries({ queryKey: auditKeys.roles(auditId) });
      queryClient.invalidateQueries({ queryKey: auditKeys.role(auditId, roleId) });
    },
  });
};

export const useDeleteAuditRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ auditId, roleId }: { auditId: string; roleId: string }) =>
      auditsApi.deleteRole(auditId, roleId),
    onSuccess: (_, { auditId }) => {
      queryClient.invalidateQueries({ queryKey: auditKeys.roles(auditId) });
    },
  });
};

export const useCancelArchivation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (auditId: string) => auditsApi.cancelArchivation(auditId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditKeys.list() });
    },
  });
};

export const useAddAuditorFirmMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ auditId, data }: { auditId: string; data: AddAuditorFirmMemberPayload }) =>
      auditsApi.addAuditorFirmMember(auditId, data),
    onSuccess: (_, { auditId }) => {
      queryClient.invalidateQueries({ queryKey: auditKeys.auditorFirmMembers(auditId) });
      queryClient.invalidateQueries({ queryKey: documentKeys.list(auditId) });
    },
  });
};

export const useChangeAuditorFirmMemberRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      auditId,
      memberId,
      data,
    }: {
      auditId: string;
      memberId: string;
      data: AuditorFirmMemberDto;
    }) => auditsApi.changeAuditorFirmMemberRole(auditId, memberId, data),
    onSuccess: (_, { auditId }) => {
      queryClient.invalidateQueries({ queryKey: auditKeys.auditorFirmMembers(auditId) });
    },
  });
};

export const useRemoveAuditorFirmMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ auditId, memberId }: { auditId: string; memberId: string }) =>
      auditsApi.removeAuditorFirmMember(auditId, memberId),
    onSuccess: (_, { auditId }) => {
      queryClient.invalidateQueries({ queryKey: auditKeys.auditorFirmMembers(auditId) });
    },
  });
};

export const useGenerateAuditReport = () =>
  useMutation<AuditArchiveJobResponse, Error, { auditId: string | number }>({
    mutationFn: ({ auditId }) => auditsApi.generateReport(auditId),
  });

export const useGenerateReportStatus = (auditId?: string, jobId?: number | null, enabled = true) =>
  useQuery<AuditArchiveJobResponse | null, Error>({
    queryKey: auditKeys.reportStatus(auditId, jobId),
    queryFn: () => auditsApi.generateReportStatus(auditId as string, jobId),
    enabled: Boolean(auditId) && enabled,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'pending' || status === 'running' ? 2000 : false;
    },
  });
